import { IconSet } from '@postscript/components';
import {
  EMAIL_SETTINGS_PAGE,
  PS_LABS,
  USAGE_BILLING_ENABLED,
} from 'components/admin/utils/feature-flags';
import useCanUsePropertyUploads from 'components/subscriberPropertyUploads/useCanUsePropertyUploads';
import { useMemo } from 'react';
import { User } from 'types';
import { NavMenuItem } from '../NavMenu';

export const useAccountMenuItems = (user: User): NavMenuItem[] => {
  const canUsePropertyUploads = useCanUsePropertyUploads() === 'allowed';
  return useMemo(
    () => [
      {
        id: 'account',
        label: user?.shop_name ?? 'Account',
        icon: IconSet.Settings2,
        isLoading: false,
        ariaLabelledBy: 'nav-account-title',
        subMenu: [
          {
            link: '$selectShop',
            label: 'Select Shop',
            id: 'account-select-shop',
          },
          {
            link: '/account/settings',
            label: 'Shop Settings',
            id: 'account-settings',
          },
          {
            link: '/profile',
            label: 'Profile',
            id: 'profile',
          },
          {
            link: '/account/users',
            label: 'Users',
            id: 'account-users',
          },
          {
            link: '/account/settings/email',
            label: 'Email Settings',
            id: 'account-email-settings',
            tag: { content: 'New', variant: 'success' },
            labsFeatureFlag: EMAIL_SETTINGS_PAGE,
          },
          {
            link: '/account/settings/messaging-reach',
            label: 'Messaging Reach',
            id: 'account-messaging-reach',
          },
          {
            link: '/account/tracking',
            label: 'Tracking',
            id: 'account-tracking',
          },
          {
            link: '/account/plan',
            label: 'Plan',
            id: 'account-plan',
            featureFlag: USAGE_BILLING_ENABLED,
            featureFlagOptOut: true,
          },
          {
            link: '/account/billing',
            label: 'Billing',
            id: 'account-billing',
            featureFlag: USAGE_BILLING_ENABLED,
            featureFlagOptOut: true,
          },
          {
            link: '/billing',
            label: 'Billing',
            id: 'account-usage-billing',
            featureFlag: USAGE_BILLING_ENABLED,
          },
          {
            link: '/account/api',
            label: 'API',
            id: 'account-api',
          },
          {
            link: '/account/ps-labs',
            label: 'PS Labs',
            id: 'account-ps-labs',
            featureFlag: PS_LABS,
          },
          {
            link: '/account/contact-card',
            label: 'Shop Contact Card',
            id: 'account-contact-card',
          },
          {
            link: '/subscriber-list-uploads',
            label: 'Subscriber List Uploads',
            id: 'subscriber-list-uploads',
          },
          ...(canUsePropertyUploads
            ? [
                {
                  link: '/subscriber-property-uploads',
                  label: 'Property Uploads',
                  id: 'subscriber-property-uploads',
                },
              ]
            : []),
          {
            link: '/bulk-subscriber-operations',
            label: 'Bulk Subscriber Operations',
            id: 'bulk-subscriber-operations',
            adminOnly: true,
          },
          { link: '$logOut', label: 'Logout', id: 'account-logout' },
        ],
      },
    ],
    [user, canUsePropertyUploads],
  );
};
